import { CarClassModel, ItemModel } from '../../api/core/core.types';
import { ItemPriceModel, ItemPriceModelBrokerRate, UnitType, UnitTypeOptions } from '../../api/pricing/pricing.types';
import React, { PureComponent } from 'react';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { WithTranslation, withTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../api/api.types';
import StatusLabel from '../forms/StatusLabel';
import { ClassifierModel } from '../../api/admin/admin.types';
import ListViewTable from './ListViewTable';

interface ItemPriceTableProps extends WithTranslation {
  items: ItemModel[];
  currency?: string;
  itemOtaCodes: ClassifierModel[];
  formItemPrices: { value: string; label: string, collectedByBroker: boolean }[];
  carClasses: CarClassModel[];
  itemPrices: ItemPriceModel[];
}

class ItemPriceTable extends PureComponent<ItemPriceTableProps> {

  statusCell = (props: any) => {
    return (<StatusLabel status={props.getValue()}/>);
  };

  carClassCell = (props: any) => {
    const { t } = this.props;
    if (props.getValue().length > 0) {
      const carList: JSX.Element[] = [];
      props.getValue().forEach((carClass: any) => {
        carList.push(
          <div
            key={carClass}
            className="flex-50 px-1"
            style={{ maxWidth: '60px' }}
          >
            {carClass}
          </div>
        );
      });
      if (props.getValue().length === 1) {
        return props.getValue()[ 0 ];
      } else {
        return (
          <div>
            <div
              className="btn btn-link font-normal p-0"
              id={`cars-tooltip-${props.row.id}`}
            >
              {t('v.car_classes', { count: props.getValue().length })}
            </div>
            <Tooltip
              modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
              placement="top"
              target={`cars-tooltip-${props.row.id}`}
              autohide={false}
            >
              <div className="d-flex flex-wrap">
                {carList}
              </div>
            </Tooltip>
          </div>
        );
      }
    }
    return (t('v.all_car_classes'));
  };

  onRowClick = (rowInfo: any) => {
    console.log(rowInfo);
    let itemGroupType = 'extras';
    if (rowInfo.original.itemGroupType === 'COVERAGE') {
      itemGroupType = 'protections';
    } else if (rowInfo.original.itemGroupType === 'FEE') {
      itemGroupType = 'fees';
    }
    window.open(`/rates/${itemGroupType}/${rowInfo.original.originalId}/view`, '_blank', 'noopener,noreferrer');
  };


  getItemOtaCode = (classifierCode: string) => {
    const itemOtaCode = this.props.itemOtaCodes.find((classifier) => classifier.code === classifierCode);
    if (itemOtaCode) {
      return itemOtaCode.stringValue;
    } else {
      return classifierCode;
    }
  };

  handleItemPrice = (itemPrice: ItemPriceModel, itemPrices: ItemPriceModelBrokerRate[], collectedByBroker: boolean) => {
    const originalCarClasses: string[] = [];
    let usedCarClasses: string[] = [];
    let hasIncludedCarClasses: boolean = false;
    if (itemPrice.carClasses.length) {
      itemPrice.carClasses.forEach((c) => {
        const price = c.itemPriceException ? c.itemPriceException.price : itemPrice.price;
        const maxPrice = (c.itemPriceException?.maxPrice) ? c.itemPriceException.maxPrice : itemPrice.maxPrice;
        const supplierRef = (c.itemPriceException?.supplierReferenceCode) ? c.itemPriceException.supplierReferenceCode : itemPrice.supplierReferenceCode;
        const rowItem: ItemPriceModelBrokerRate = {
          ...itemPrice,
          id: Number(itemPrice.id.toString().concat(c.id ? c.id.toString() : '')),
          price: price,
          maxPrice: maxPrice,
          supplierReferenceCode: supplierRef,
          selectedCarClasses: c.acrissCodes,
          originalId: itemPrice.id,
          itemOtaCodeCode: this.getItemOtaCode(itemPrice.itemOtaCode),
          collectedByBroker: collectedByBroker
        };
        itemPrices.push(rowItem);
        if (c.itemPriceException) {
          usedCarClasses = usedCarClasses.concat(c.acrissCodes);
        }
        if (!hasIncludedCarClasses) {
          hasIncludedCarClasses = c.itemPriceException === null;
        }
      });
      if (this.props.carClasses.length > usedCarClasses.length && usedCarClasses.length > 0 && !hasIncludedCarClasses) {
        this.props.carClasses.forEach((carClass) => {
          if (!usedCarClasses.includes(carClass.acrissCode)) {
            originalCarClasses.push(carClass.acrissCode);
          }
        });
        itemPrices.push({
          ...itemPrice,
          selectedCarClasses: originalCarClasses,
          originalId: itemPrice.id,
          itemOtaCodeCode: this.getItemOtaCode(itemPrice.itemOtaCode),
          collectedByBroker: collectedByBroker
        });
      }
    } else {
      itemPrices.push({
        ...itemPrice,
        selectedCarClasses: [] as string[],
        originalId: itemPrice.id,
        itemOtaCodeCode: this.getItemOtaCode(itemPrice.itemOtaCode),
        collectedByBroker: collectedByBroker
      });
    }
  };

  getDate = (props: any) => {
    return dayjs(props.getValue()).format(DATE_FORMAT);
  };

  priceCell = (props: any) => {
    const { t } = this.props;
    const objectUnit = UnitTypeOptions.find((select) => select.value === props.row.original.unitType);
    let priceUnit = t('v.item_prices.price.per_day', { currency: this.props.currency ? this.props.currency : '' });
    if (objectUnit?.value === UnitType.PER_CENT) {
      priceUnit = t('v.item_prices.price.percent');
    } else if (objectUnit?.value === UnitType.FIXED) {
      priceUnit = t('v.item_prices.price.fixed');
    }
    return `${props.getValue()} ${priceUnit}`;
  };

  getItemPriceName = (props: any) => {
    if (this.props.items) {
      const item = this.props.items.find((i) => i.id === props.getValue());
      if (item) {
        return item.name;
      }
    }

    return String(props.getValue());
  };

  getCollectedByBroker = (props: any) => {
    if (props.getValue()) {
      return 'Yes';
    } else {
      return 'No';
    }
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.broker_rate.mandatory_item.name'), accessorKey: 'coreItemId', cell: this.getItemPriceName
      },
      { header: t('ct.broker_rate.mandatory_item.ota_code'), accessorKey: 'itemOtaCodeCode' },
      {
        header: t('ct.broker_rate.mandatory_item.price'), accessorKey: 'price', cell: this.priceCell
      },
      {
        header: 'Collected by broker', accessorKey: 'collectedByBroker', cell: this.getCollectedByBroker
      },
      {
        header: t('ct.broker_rate.mandatory_item.max_price'), accessorKey: 'maxPrice'
      },
      {
        header: t('ct.broker_rate.mandatory_item.supplier_ref'), accessorKey: 'supplierReferenceCode'
      },
      {
        header: t('ct.broker_rate.mandatory_item.status'), accessorKey: 'statusType', cell: this.statusCell
      },
      {
        header: t('ct.broker_rate.mandatory_item.car_classes'),
        accessorKey: 'selectedCarClasses',
        cell: this.carClassCell
      },
      {
        header: 'Valid from', accessorKey: 'validFrom', cell: this.getDate
      }
    ];
  }

  render() {
    const { t } = this.props;
    const itemPrices: ItemPriceModelBrokerRate[] = [];
    this.props.formItemPrices.forEach((fip) => {
      const itemPrice = this.props.itemPrices.find((ip) => ip.id === Number(fip.value));
      if (itemPrice) {
        this.handleItemPrice(itemPrice, itemPrices, fip.collectedByBroker);
      }
    });

    return (
      <div className="table-list fees-table">
        <ListViewTable data={itemPrices} columns={this.defineColumns()} getTrProps={this.onRowClick}/>
      </div>);
  }
}

export default withTranslation()(ItemPriceTable);